// ===== 編集 (備考のみ) =====

import { useEffect, useState } from "react";
import { Stack, Typography, Paper, TextField, Alert, Button, Chip } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { PageContext } from "../PageContext";
import { dataEntityText } from "../../../components/links/EntityLinks";
import { issuanceRow } from "../../../schema/console/issuance";
import { DetailGuard, useDetailFetch, useDetailRetry } from "../../../components/DetailFetch";
import { materialIdRange } from "./helpers";

export function IssuanceEditPage({ pageContext }: { pageContext: PageContext }) {
  const { id } = useParams<{ id: string }>();
  const api = pageContext.api;
  const navigate = useNavigate();
  const [remarks, setRemarks] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [retryCount, retry] = useDetailRetry();
  const state = useDetailFetch<issuanceRow>(() => {
    if (!id) {
      return Promise.resolve(null);
    }
    return api.issuance.getFromId({ id });
  }, [api, id, retryCount]);

  useEffect(() => {
    if (state.status === "ready") {
      setRemarks(state.data.remarks ?? "");
    }
  }, [state]);

  const submit = async (info: issuanceRow) => {
    setSubmitting(true);
    try {
      await api.issuance.patch({ id: info.id, remarks: remarks || null });
      navigate(`/console/issuance/${info.id}/`);
    } catch {
      setError("更新に失敗しました。もう一度お試しください");
      setSubmitting(false);
    }
  };

  return (
    <DetailGuard state={state} notFoundLabel={`発行 ${id}`} onRetry={retry}>
      {(info) => <IssuanceEditForm info={info} remarks={remarks} setRemarks={setRemarks} error={error} submitting={submitting} onSubmit={() => submit(info)} />}
    </DetailGuard>
  );
}

function IssuanceEditForm({ info, remarks, setRemarks, error, submitting, onSubmit }: {
  info: issuanceRow; remarks: string; setRemarks: (value: string) => void; error: string | null; submitting: boolean; onSubmit: () => void;
}) {
  const range = materialIdRange(info.Materials);

  return (
    <Stack p={3} gap={2} maxWidth={900} mx="auto">
      <Typography variant="h4" fontWeight="bold">発行情報の編集</Typography>
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Stack gap={2} maxWidth={600} mx="auto">
          <Alert severity="info" sx={{ py: 0.5 }}>発行枚数・連番は不変（既に印刷されたシール整合性のため）。備考のみ編集可。</Alert>
          <Chip label={dataEntityText("issuance", info.id)} />
          <Chip label={`発行総数: ${info.Materials.length} 枚`} />
          <Chip label={`QR範囲: 材料 ${range.head} 〜 材料 ${range.tail}`} />
          <TextField label="備考" multiline minRows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} fullWidth />
          {error && <Alert severity="error">{error}</Alert>}
          <Stack alignItems="center">
            <Button variant="contained" size="large" disabled={submitting} onClick={onSubmit} sx={{ py: 1.5, width: "60%" }}>更新</Button>
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  );
}
